/**
 * Module 'history' (ADR 0109): hourly and daily rollups per workspace and the day's ended
 * sessions, for every view that reads a date range.
 */
import type { Id, Ts } from '../index.ts';
import type { AgentKind } from './adapters.ts';

export const HISTORY_EXT = 'history';

export type HistoryBucketSize = 'hour' | 'day';
export const HISTORY_BUCKETS: readonly HistoryBucketSize[] = ['hour', 'day'];

/** Every metric a rollup counts, with the dimensions it can be grouped by. */
export const HISTORY_METRICS = {
  'sessions.started': ['agent', 'project', 'machine'],
  'sessions.ended': ['agent', 'outcome', 'project', 'machine'],
  turns: ['agent', 'model', 'project'],
  toolCalls: ['agent', 'model', 'project'],
  'tokens.input': ['agent', 'model', 'project'],
  'tokens.output': ['agent', 'model', 'project'],
  'tokens.cacheRead': ['agent', 'model', 'project'],
  'cost.usd': ['agent', 'model', 'project'],
  'machines.onlineMinutes': ['machine'],
  'machines.online': [],
  missions: ['action'],
  approvals: ['action', 'result'],
  landings: ['action', 'result'],
  incidents: ['action'],
  audit: ['action', 'result'],
  'wire.patches': [],
  'wire.bytes': [],
} as const satisfies Record<string, readonly string[]>;

export type HistoryMetric = keyof typeof HISTORY_METRICS;
export const HISTORY_METRIC_NAMES = Object.keys(HISTORY_METRICS) as HistoryMetric[];
/** Metrics whose bucket value is a peak, not a sum. */
export const HISTORY_PEAK_METRICS: readonly HistoryMetric[] = ['machines.online'];
export const isHistoryMetric = (x: unknown): x is HistoryMetric => typeof x === 'string' && Object.hasOwn(HISTORY_METRICS, x);

/** Limits of one series request. */
export const HISTORY_MAX_METRICS = 12;
export const HISTORY_MAX_GROUPS = 20;
export const HISTORY_MAX_HOURS = 31 * 24;
export const HISTORY_MAX_DAYS = 1830;
export const HISTORY_SESSIONS_PAGE = 100;

/**
 * One bucket's counters: metric -> dimension key -> value. A key is `dim=value` pairs joined by
 * `|` in the metric's dimension order ('' for a metric without dimensions).
 */
export type HistoryCounters = Partial<Record<HistoryMetric, Record<string, number>>>;

/** One UTC day as stored: its hours (00..23) and its total. */
export interface HistoryDayFile {
  day: string;
  hours: Record<string, HistoryCounters>;
  total: HistoryCounters;
}

export interface HistorySeries {
  metric: HistoryMetric;
  /** The group value ('' when not grouped, 'other' for what passed the group limit). */
  group: string;
  values: number[];
}

export interface HistorySeriesResult {
  from: Ts;
  to: Ts;
  bucket: HistoryBucketSize;
  groupBy: string | null;
  /** Each bucket's start, epoch ms, UTC. */
  buckets: Ts[];
  series: HistorySeries[];
  /** The first day this workspace has history for (null: none yet). */
  since: string | null;
}

export type HistorySessionOutcome = 'done' | 'empty' | 'gone';

/** An ended session as the history keeps it (ADR 0109); the transcript stays on its machine. */
export interface HistorySession {
  id: Id;
  machineId: Id;
  machine: string;
  projectId: Id | null;
  project: string | null;
  title: string;
  agent: AgentKind;
  model: string | null;
  origin: 'interactive' | 'orgus';
  startedAt: Ts;
  endedAt: Ts;
  turns: number;
  toolCalls: number;
  tokens: { input: number; output: number; cacheRead: number };
  costUsd: number;
  outcome: HistorySessionOutcome;
  /** When it asked a person (needs you), epoch ms, at most 20. */
  asked?: Ts[];
}

export interface HistorySessionsResult {
  from: Ts;
  to: Ts;
  sessions: HistorySession[];
  /** Pass back as `cursor` for the next page; null on the last. */
  next: string | null;
  /** Days in range whose segment could not be read. */
  unreadable: string[];
}

export interface HistoryRetention {
  /** Hourly buckets, days (null: for ever). */
  hourDays: number | null;
  /** Daily rollups, days. */
  dayDays: number | null;
  /** Session segments, days. */
  segmentDays: number | null;
}

export const HISTORY_RETENTION_DEFAULT: HistoryRetention = { hourDays: 90, dayDays: 1825, segmentDays: 365 };

export interface HistoryIndexEntry {
  day: string;
  sessions: number;
  events: number;
  bytes: number;
}

export interface HistoryRetentionState {
  retention: HistoryRetention;
  days: number;
  segments: number;
  segmentBytes: number;
  since: string | null;
}

export interface HistoryState {
  since: string | null;
  /** The open hour's start. */
  hour: Ts;
}

/** UTC day of an epoch ms. */
export const historyDay = (t: number): string => new Date(t).toISOString().slice(0, 10);
export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;
/** Parse `from`/`to`: epoch ms or YYYY-MM-DD (UTC midnight; `to` as a date means its end). */
export function parseHistoryTime(v: string | null, end = false): number | null {
  if (v == null || v === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const t = Date.parse(`${v}T00:00:00Z`);
    return Number.isFinite(t) ? t + (end ? DAY_MS : 0) : null;
  }
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
